'use strict';

/**
 * @puffin-core/env — canonical NODE_ENV classifier for the ParcelPuffin ecosystem
 * (HARD-JWTS-001 / A-3).
 *
 * The ecosystem runs three NODE_ENV modes: `development` (local only),
 * `preview`, and `production` (both deployed — see "Environment Modes" in
 * CLAUDE.md), plus `test` under jest. Security-sensitive relaxations (dev
 * fallback signing secrets, dev-origin CORS reflection, private-range webhook
 * targets) must key off an explicit ALLOW-LIST of local environments — never
 * off `NODE_ENV !== 'production'`, which silently treats `preview`, `staging`,
 * unset, and typo'd values as safe. That inverted check is exactly the
 * forgery vector finding A-3 closed.
 *
 * This is a deliberate ZERO-DEPENDENCY leaf package so the lowest-level
 * consumers — `@puffin-core/webhooks` among them — can share the one
 * classifier instead of mirroring it, without inverting the dependency graph
 * (they must not import the heavier `@puffin-core/middleware`, which itself
 * re-exports from here). (`@puffin-core/field-encryption` was a consumer until
 * Q-L16-98 deleted its dev-fallback key, the only thing it classified for.)
 *
 * Exact-match, case-sensitive, no normalization: an unrecognized or
 * misspelled NODE_ENV classifies as DEPLOYED (fail-closed), which surfaces
 * the misconfiguration at boot instead of silently weakening security.
 *
 * @module @puffin-core/env/nodeEnv
 */

const { processEnv } = require('./processEnv');

/** The two environments treated as local development. Frozen allow-list. */
const LOCAL_DEV_ENVS = Object.freeze(['development', 'test']);

/**
 * The deployed PUBLIC environments — the modes a live DigitalOcean app serves real traffic in.
 * `preview` is ParcelPuffin's soft-launch mode and is live at its production domain (the
 * "Environment Modes" table in CLAUDE.md), so it belongs here beside `production`. Pinned to the
 * NODE_ENV every DO spec component declares by `tests/cross-app/contracts/deployedNodeEnvs.contract`,
 * so a spec that introduces another mode fails a test, never a boot. Exact match, frozen.
 */
const DEPLOYED_PUBLIC_ENVS = Object.freeze(['production', 'preview']);

/**
 * True only when the process runs in true local development — `NODE_ENV`
 * exactly `development` or `test`. Everything else (`production`, `preview`,
 * `staging`, unset, casing variants, arbitrary strings) is DEPLOYED and must
 * be treated fail-closed by consumers.
 *
 * @param {NodeJS.ProcessEnv} [env=processEnv()] - Environment to classify.
 * @returns {boolean}
 */
function isLocalDevEnv(env = processEnv()) {
  return LOCAL_DEV_ENVS.includes(env.NODE_ENV);
}

/**
 * True only when `NODE_ENV` is exactly `development`. Use where the distinction
 * from `test` matters (e.g. the whole-file dev CORS bypass, which must NOT
 * relax under jest). For the security allow-list use {@link isLocalDevEnv}.
 *
 * @param {NodeJS.ProcessEnv} [env=processEnv()] - Environment to classify.
 * @returns {boolean}
 */
function isDevelopmentEnv(env = processEnv()) {
  return env.NODE_ENV === 'development';
}

/**
 * True only when `NODE_ENV` is exactly `test`.
 *
 * @param {NodeJS.ProcessEnv} [env=processEnv()] - Environment to classify.
 * @returns {boolean}
 */
function isTestEnv(env = processEnv()) {
  return env.NODE_ENV === 'test';
}

/*
 * There is deliberately NO strict-production predicate (SEC-B deployment-tier unit, 2026-09-30).
 * `isProductionEnv` (`NODE_ENV === 'production'`) was removed: parcelpuffin's production runs
 * `NODE_ENV=preview`, and every safety decision keyed on the strict check — 5xx masking, the
 * verification bypass, the mock-Stripe fallback, the CSP upgrade — fell open there. A deployed
 * tier is `!isLocalDevEnv(env)`; the `no-production-node-env-check` rule keeps the comparison out.
 */

/**
 * True only when `NODE_ENV` is exactly one of {@link DEPLOYED_PUBLIC_ENVS} — a live public
 * deployment. Not the complement of {@link isLocalDevEnv}: `staging`, `perf`, unset and typo'd
 * values are neither, and a consumer must decide what they mean (fail-closed).
 *
 * @param {NodeJS.ProcessEnv} [env=processEnv()] - Environment to classify.
 * @returns {boolean}
 */
function isDeployedPublicEnv(env = processEnv()) {
  return DEPLOYED_PUBLIC_ENVS.includes(env.NODE_ENV);
}

/**
 * What a log line, a metrics resource or a health payload calls a process whose `NODE_ENV` is not set (owner rulings
 * Q-EE-42 "Fail closed" and Q-UT-1, UNSET-TIER-FAILS-CLOSED-001). It is never `development`: an unset tier is judged
 * DEPLOYED, so a label that said `development` would contradict the judgement made beside it.
 */
const UNSET_NODE_ENV_LABEL = 'unset';

/**
 * The ONE label for a process's tier: its `NODE_ENV` exactly as set, or {@link UNSET_NODE_ENV_LABEL} when it is unset
 * or empty. A LABEL only: it decides nothing. A judgement asks {@link isLocalDevEnv} / {@link isDeployedPublicEnv},
 * which read an unset tier as deployed.
 *
 * @param {NodeJS.ProcessEnv} [env=processEnv()] - Environment to describe.
 * @returns {string}
 */
function nodeEnvLabel(env = processEnv()) {
  const nodeEnv = env.NODE_ENV;
  return typeof nodeEnv === 'string' && nodeEnv !== '' ? nodeEnv : UNSET_NODE_ENV_LABEL;
}

module.exports = {
  LOCAL_DEV_ENVS,
  DEPLOYED_PUBLIC_ENVS,
  UNSET_NODE_ENV_LABEL,
  nodeEnvLabel,
  isDeployedPublicEnv,
  isLocalDevEnv,
  isDevelopmentEnv,
  isTestEnv,
};
